import { useState, useEffect } from 'react'
import { Client, Account, Teams, Functions, Databases, AppwriteException } from 'appwrite'
import { atom, useAtom } from 'jotai'
import { createTheme } from '@mui/material/styles';


const client = new Client()
    .setEndpoint(process.env.REACT_APP_ENDPOINT)
    .setProject(process.env.REACT_APP_PROJECT_ID)

const account = new Account(client)
const teams = new Teams(client);
const functions = new Functions(client);
const db = new Databases(client);



// Santize the given string so it can be used for Appwrite IDs
export function sanitize(text) {
    try {
        return text
            .replace(/&/g, "and")
            .replace(/[^a-zA-Z0-9\s_]/g, '')
            .replace(/ /g, '-')
            .toLowerCase()
    } catch (error) {
        return null
    }
}

// Convert kebab-case to camelCase
export function kebabToCamel(str) {
    return str.replace(/-([a-z])/g, function (g) {
        return g[1].toUpperCase();
    });
}

// Convert camelCase to kebab-case
export function camelToKebab(str) {
    return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

// Convert camelCase to Title Case
export function camelToTitle(str) {
    return str.replace(/^./, match => match.toUpperCase()).replace(/(?<!^)([A-Z])/g, ' $1')
}

// Convert kebab-case to Title Case
export function kebabToTitle(str) {
    return str.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')
}

// Convert Title Case to kebab-case
export function titleToKebab(str) {
    return str.replace(/ /g, '-').replace(/[A-Z]*/g, match => match.toLowerCase());
}

export function toTitleCase(str) {
    return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}

// Get file extension from string
export function extension(filename) {
    return filename.split('.').pop();
}



// Themes
export const ThemeAtom = atom(createTheme({
    cssVariables: {
        colorSchemeSelector: 'data-toolpad-color-scheme',
    },
    colorSchemes: {
        light: {
            palette: {
                background: {
                    default: '#F9F9FE',
                    paper: '#EEEEF9',
                },
            },
        },
        dark: {
            palette: {
                background: {
                    default: '#121212',
                    paper: '#121212',
                },
            },
        },
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 600,
            lg: 1200,
            xl: 1536,
        },
    },
}));

// Export the atom for use in components
export const UseTheme = () => {
    const [theme] = useAtom(ThemeAtom);
    return theme;
};



// TODO: these are deprecated, remove

// User's team list
export const TeamList = atom([]);

// User's membership data in current team
export const TeamMember = atom(null);

// Fetch teams and update the teams list
export const fetchTeams = async (setTeamsList, setAdminUser) => {
    try {
        const result = await teams.list();
        setTeamsList(result.teams);
        // console.log("Teams:", result.teams);

        // Set if user is an admin
        if (result.teams.some(team => team.$id === 'takeoff-monkey')) {
            setAdminUser(true);
        } else {
            setAdminUser(false);
        }
    } catch (err) {
        console.error("Error fetching teams:", err);
        setAdminUser(false);
    }
};
export const AdminUser = atom(null)


// FIXME: are these still necessary? Clean up
// These values set in DashboardMenu
export const UserSession = atom(null)
export const UserMaster = atom(false)
export const UserOwner = atom(false)

export const TeamsList = atom([])
export const CurrentTeam = atom(0)

export const TeamURL = atom(null)


export const login = async (email, password) => {
    try {
        const session = await account.createEmailPasswordSession(email, password)
        const user = await account.get()
        return user
    } catch (error) {
        const appwriteError = error instanceof AppwriteException
        throw new Error(appwriteError.message)
    }
}

export const logout = async () => {
    try {
        return account.deleteSession("current")
    } catch (error) {
        const appwriteError = error instanceof AppwriteException
        throw new Error(appwriteError.message)
    }
}

export const checkUser = async () => {
    try {
        const user = await account.get()
        return user
    } catch (error) {
        const appwriteError = error instanceof AppwriteException
        throw new Error(appwriteError.message)
    }
}

export const register = async (username, email, password) => {
    try {
        const name_id = username.replace(/ /g, "_")
        await account.create(
            name_id,
            email,
            password,
            username
        ).then(() => {
            return account.createEmailPasswordSession(email, password)
        })
    } catch (error) {
        const appwriteError = error instanceof AppwriteException
        throw new Error(appwriteError.message)
    }
}


// TODO: deprecated, remove
/**
 * @setDefaultData
 * Sets default data for a document key, including creating a new attribute in the collection if necessary.
 * 
 * @param {Object} userSession The User object from UserSession.
 * @param {String} widgetName Name of the document that will hold the widget's data. This should be the same across the board, so values can be accessed and conflicting documents for the same widget aren't accidentally created.
 * @param {String} docKey Name of the key to write to/create in the document.
 * @param {Object} keyData Data to write to the document key. Should be an object specifying necessary parameters (at minimum needs a type value; check Database document types for more info).
 * @param {Object} defaultData Default data to write to the document key. This is what will be written if the document key doesn't exist yet.
 * 
 * @returns {Promise} The data written to the document key. Should be called with await to get the value.
 */
export async function setDefaultData(userSession, widgetName, docKey, keyData, defaultData) {
    // If existing doc data already exists, let's get that instead
    try {
        const data = await db.getDocument(
            'widget-data',
            userSession.$id,
            widgetName
        );
        
        if ((!Array.isArray(data[docKey]) && data[docKey] !== undefined) || (Array.isArray(data[docKey]) && data[docKey].length > 0)) {
            return data[docKey];
        } else {
            throw new Error('Document key does not exist; creating...');
        }
    } catch (err) {
        try {
            // Create a new attribute
            await functions.createExecution(
                'db-create-collection',
                JSON.stringify({
                    user: userSession,
                    params: { [docKey]: keyData }
                }),
                false,
                '/',
                'GET'
            );
            // Create a new document or update existing (this sets the default data)
            try {
                const data = await db.createDocument(
                    'widget-data',
                    userSession.$id,
                    widgetName,
                    Array.isArray(defaultData) ? { [docKey]: defaultData } : { [docKey]: [defaultData] }
                );
                return data[docKey];
            } catch (err) {
                const data = await db.updateDocument(
                    'widget-data',
                    userSession.$id,
                    widgetName,
                    Array.isArray(defaultData) ? { [docKey]: defaultData } : { [docKey]: [defaultData] }
                );
                return data[docKey];
            }
        } catch (err) {
            console.error('Error creating document:', err);
            return defaultData;
        }
    }
}